
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, CheckCircle, XCircle, PalmtreeIcon, Trash2 } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

type VacationRequest = Tables<"vacation_requests">;

interface VacationHistoryProps {
  vacationRequests: VacationRequest[] | undefined;
}

export const VacationHistory = ({ vacationRequests }: VacationHistoryProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDelete = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('vacation_requests')
        .delete()
        .eq('id', requestId)
        // Only allow deletion of pending requests by the request owner
        .eq('status', 'pending');

      if (error) throw error;

      toast({
        title: "Request deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: ["vacation_requests"] });
    } catch (error) {
      console.error('Error deleting request:', error);
      toast({
        title: "Error deleting request",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl font-bold">Vacation History</CardTitle>
      </CardHeader>
      <CardContent>
        {!vacationRequests?.length ? (
          <div className="flex flex-col items-center justify-center text-center py-12 space-y-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#D3E4FD] to-[#F1F0FB] rounded-full blur-xl opacity-70"></div>
              <PalmtreeIcon className="h-20 w-20 text-[#8E9196] relative z-10" />
            </div>
            <div className="space-y-2 max-w-sm">
              <p className="text-xl font-medium text-gray-900">Time to Plan Your Next Adventure!</p>
              <p className="text-sm text-gray-500">
                Your vacation history is empty. Why not start planning your next break? 
                Take some time off to recharge and create memorable experiences.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {vacationRequests?.map((vacation) => (
              <div
                key={vacation.id}
                className="flex items-center justify-between p-4 border rounded-lg bg-white"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {vacation.status === "approved" ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : vacation.status === "rejected" ? (
                      <XCircle className="h-5 w-5 text-red-500" />
                    ) : (
                      <Calendar className="h-5 w-5 text-orange-500" />
                    )}
                    <span className="font-medium">
                      {new Date(vacation.start_date).toLocaleDateString()} - {new Date(vacation.end_date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {vacation.is_half_day ? "Half Day" : "Full Day"} • {vacation.reason}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm ${
                      vacation.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : vacation.status === "rejected"
                        ? "bg-red-100 text-red-800"
                        : "bg-orange-100 text-orange-800"
                    }`}
                  >
                    {vacation.status?.charAt(0).toUpperCase() + vacation.status?.slice(1)}
                  </span>
                  {vacation.status === 'pending' && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete your vacation request.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(vacation.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
